"use client";

import { c as _c } from "react/compiler-runtime";
import type { FC } from "react";
import { useRef } from "react";
import { useResizeObserver } from "@/hooks/use-resize-observer";
import { useWorldTour } from "@/hooks/use-world-tour";
import { cn } from "@/lib/utils";
import { BreakoutWrapper } from "@/ui/atoms/breakout-wrapper";
import StarFieldBackground from "@/ui/world-tour/star-field-background";
import { WorldCountryFlagCounts } from "@/ui/world-tour/world-country-flag-counts";
import { AnimatePresence, motion } from "motion/react";
const visitorData = [["840", 432], ["702", 408], ["156", 174], ["643", 27], ["344", 21], ["484", 16], ["276", 11], ["356", 11], ["704", 7], ["036", 6], ["250", 6], ["826", 6], ["792", 6], ["076", 5], ["124", 5], ["804", 5], ["360", 4], ["392", 4], ["372", 3], ["398", 3], ["528", 3], ["608", 3], ["764", 3], ["100", 2], ["818", 2], ["348", 2], ["368", 2], ["380", 2], ["752", 2], ["004", 1], ["040", 1], ["170", 1], ["196", 1], ["203", 1], ["208", 1], ["724", 1], ["246", 1], ["376", 1], ["388", 1], ["400", 1], ["438", 1], ["504", 1], ["458", 1], ["512", 1], ["586", 1], ["642", 1], ["688", 1], ["682", 1], ["158", 1], ["862", 1], ["710", 1]] as const satisfies readonly [string, number][];
const WorldTour: FC = () => {
  const $ = _c(28);
  const d3ContainerRef = useRef(null);
  const parentContainerRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      visitorData
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    containerRef,
    currentCountry,
    currentVisitors
  } = useWorldTour(t0);
  const {
    height
  } = useResizeObserver(parentContainerRef);
  let t1;
  if ($[1] !== height) {
    t1 = <StarFieldBackground height={height} />;
    $[1] = height;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="absolute inset-0 bg-[oklch(0.1370608055115447_0.03597153479968494_258.52581130794215)]/30 backdrop-blur-sm" />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 0,
      x: 50
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      opacity: 1,
      x: 0,
      transition: {
        delay: 0.2,
        type: "spring",
        visualDuration: 0.3,
        bounce: 0.4,
        damping: 75
      }
    };
    t5 = {
      opacity: 0,
      x: -50
    };
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[7] !== currentCountry.countryFlag || $[8] !== currentCountry.countryName || $[9] !== currentCountry.flagAspectRatio || $[10] !== currentVisitors) {
    t6 = <div className="absolute inset-x-0 inset-y-0"><WorldCountryFlagCounts countryName={currentCountry.countryName} flagUrl={currentCountry.countryFlag} visitors={currentVisitors} flagAspectRatio={currentCountry.flagAspectRatio} /></div>;
    $[7] = currentCountry.countryFlag;
    $[8] = currentCountry.countryName;
    $[9] = currentCountry.flagAspectRatio;
    $[10] = currentVisitors;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== currentCountry.countryName || $[13] !== t6) {
    t7 = <div className="relative isolate mx-auto mt-0 flex w-full flex-row justify-start px-0">{t2}<motion.div key={currentCountry.countryName} className="motion-ease-in-out-quad relative z-10 flex transform items-start justify-center sm:items-center" initial={t3} animate={t4} exit={t5}>{t6}</motion.div></div>;
    $[12] = currentCountry.countryName;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className={cn("pointer-events-none absolute bottom-[10%] left-1/2 h-6 w-[60%] -translate-x-1/2 rounded-full bg-linear-to-bl from-black/10 via-black/25 to-black/30 mix-blend-soft-light blur-sm sm:bottom-[5%]")} />;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t10;
  let t11;
  let t12;
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      opacity: 0,
      y: 20
    };
    t10 = {
      opacity: 1,
      y: 0
    };
    t11 = {
      duration: 0.8
    };
    t12 = <svg ref={d3ContainerRef} className="h-full w-full" />;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
    $[19] = t9;
  } else {
    t10 = $[16];
    t11 = $[17];
    t12 = $[18];
    t9 = $[19];
  }
  let t13;
  if ($[20] !== containerRef) {
    t13 = <BreakoutWrapper><div className="mx-auto max-w-5xl"><div className="relative flex h-auto min-w-screen items-center justify-center sm:min-h-[60vh] sm:min-w-full">{t8}<motion.div ref={containerRef} className="relative top-1/2 bottom-1/2 z-10 mx-auto flex aspect-3/4 h-auto w-full items-center justify-center align-middle drop-shadow-sm sm:aspect-square" initial={t9} animate={t10} transition={t11}>{t12}</motion.div></div></div></BreakoutWrapper>;
    $[20] = containerRef;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t13 || $[23] !== t7) {
    t14 = <AnimatePresence mode="popLayout" custom={-1}><div className="mx-auto w-full justify-center">{t7}{t13}</div></AnimatePresence>;
    $[22] = t13;
    $[23] = t7;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t1 || $[26] !== t14) {
    t15 = <div ref={parentContainerRef} className="relative w-full bg-transparent sm:space-y-2">{t1}{t14}</div>;
    $[25] = t1;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  return t15;
};
export default WorldTour;